import type { EpisodeBuildStatus } from './buildProgress'
import type { Episode } from './types'

type Props = {
  episodes: Episode[]
  cap: number
  onMoveUp: (index: number) => void
  onMoveDown: (index: number) => void
  onRemove: (id: string) => void
  onClear: () => void
  // When set, the tray is in build mode: row controls are replaced by
  // per-episode progress and the ordering is frozen.
  buildStatuses?: Record<string, EpisodeBuildStatus>
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function BuildStatusCell({ status }: { status: EpisodeBuildStatus }) {
  switch (status.phase) {
    case 'waiting':
      return <span className="selected-tray__status-label">Waiting…</span>
    case 'downloading':
      return (
        <>
          <span
            className="music-player__spinner selected-tray__spinner"
            aria-hidden="true"
          />
          <span className="selected-tray__status-label">Downloading…</span>
        </>
      )
    case 'queued':
      return (
        <span className="selected-tray__status-label">Waiting to compress…</span>
      )
    case 'encoding':
      return (
        <>
          <div
            className="selected-tray__bar"
            role="progressbar"
            aria-valuenow={status.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Compressing"
          >
            <div
              className="selected-tray__bar-fill"
              style={{ width: `${status.percent}%` }}
            />
          </div>
          <span className="selected-tray__status-label">{status.percent}%</span>
        </>
      )
    case 'done':
      return (
        <span className="selected-tray__status-label selected-tray__status-label--done">
          Ready ✓
        </span>
      )
    case 'failed':
      return (
        <span className="selected-tray__status-label selected-tray__status-label--failed">
          Failed
        </span>
      )
  }
}

export default function SelectedEpisodesTray({
  episodes,
  cap,
  onMoveUp,
  onMoveDown,
  onRemove,
  onClear,
  buildStatuses,
}: Props) {
  if (episodes.length === 0) {
    return (
      <div className="selected-tray selected-tray--empty">
        <p className="podcast-empty">
          Your selected episodes will appear here. Search above to start.
        </p>
      </div>
    )
  }

  const handleClear = () => {
    if (window.confirm('Remove all selected episodes?')) {
      onClear()
    }
  }

  return (
    <div className="selected-tray">
      <div className="selected-tray__header">
        <h3 className="selected-tray__title">
          {episodes.length} of {cap} selected
        </h3>
        {!buildStatuses && (
          <button type="button" className="podcast-link-button" onClick={handleClear}>
            Clear all
          </button>
        )}
      </div>
      <ol className="selected-tray__list">
        {episodes.map((ep, i) => {
          const minutes = Math.round(ep.audioLengthSec / 60)
          return (
            <li key={ep.id} className="selected-tray__row">
              <span className="selected-tray__index">{pad2(i + 1)}</span>
              {ep.thumbnail && (
                <img
                  src={ep.thumbnail}
                  alt=""
                  className="selected-tray__thumb"
                  loading="lazy"
                />
              )}
              <div className="selected-tray__body">
                <p className="selected-tray__ep-title">{ep.title}</p>
                <p className="selected-tray__meta">
                  {ep.podcastTitle}
                  {minutes > 0 && ` · ${minutes} min`}
                </p>
              </div>
              {buildStatuses ? (
                <div className="selected-tray__status">
                  <BuildStatusCell
                    status={buildStatuses[ep.id] ?? { phase: 'waiting' }}
                  />
                </div>
              ) : (
                <div className="selected-tray__buttons">
                  <button
                    type="button"
                    className="btn selected-tray__btn"
                    onClick={() => onMoveUp(i)}
                    disabled={i === 0}
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn selected-tray__btn"
                    onClick={() => onMoveDown(i)}
                    disabled={i === episodes.length - 1}
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn selected-tray__btn"
                    onClick={() => onRemove(ep.id)}
                    aria-label="Remove"
                  >
                    ✕
                  </button>
                </div>
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}
